import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Clock3,
  AlertTriangle,
  Filter,
  RefreshCw,
  Activity,
  Target,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";

const LeadAgingReportSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">
      <div className="h-6 w-48 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({
          length: 4,
        }).map((_, index) => (
          <div key={index} className="h-24 bg-gray-100 rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="h-[380px] bg-gray-100 rounded-xl" />

        <div className="h-[380px] bg-gray-100 rounded-xl" />
      </div>
    </div>
  );
};

const LeadAgingReportError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-lg font-semibold text-red-600">
        Failed to load Lead Aging Report
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const BAR_COLORS = ["#22C55E", "#3B82F6", "#F59E0B", "#F97316", "#DC2626"];

const LeadAgingReportCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getLeadAgingReport.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const worstBucket = useMemo(() => {
    if (!data?.chart?.length) return null;

    return [...data.chart].sort((a, b) => b.count - a.count)[0];
  }, [data]);

  if (loading) {
    return <LeadAgingReportSkeleton />;
  }

  if (error) {
    return <LeadAgingReportError onRetry={fetchReport} />;
  }

  const summary = data.summary;

  const stats = [
    {
      title: "Active Leads",
      value: summary.totalActiveLeads,
      icon: Activity,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Critical Leads",
      value: summary.criticalLeads,
      icon: AlertTriangle,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: "Critical %",
      value: `${summary.criticalPercentage}%`,
      icon: Target,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title: "Risk Status",
      value: summary.riskStatus,
      icon: Clock3,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <Clock3 size={18} className="text-red-600" />

            <h2 className="font-semibold text-lg">Lead Aging Report</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {stats.map((item) => {
            const Icon = item.icon;

            return (
              <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                <div className="flex justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{item.title}</p>

                    <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                      {item.value}
                    </h3>
                  </div>

                  <Icon size={20} className={item.color} />
                </div>
              </div>
            );
          })}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="border rounded-xl p-4 h-[380px]">
              <h3 className="font-semibold mb-4">Lead Aging Distribution</h3>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart}>
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                dataKey="bucket"
                tick={{ fontSize: 12 }}
                angle={-15}
                textAnchor="end"
                />

                  <YAxis />

                  <Tooltip />

                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {data.chart.map((entry, index) => (
                      <Cell key={index} fill={BAR_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={18} className="text-red-600" />

                <h3 className="font-semibold">Aging Analysis</h3>
              </div>

              {worstBucket && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 border rounded-xl p-5">
                  <h4 className="font-bold text-xl">{worstBucket.bucket}</h4>

                  <p className="text-sm text-gray-500 mt-1">
                    Highest Aging Category
                  </p>

                  <div className="mt-5">
                    <p className="text-3xl font-bold text-red-600">
                      {worstBucket.count}
                    </p>

                    <p className="text-sm text-gray-500">
                      Leads Currently Waiting
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 space-y-3">
                {Object.entries(data.aging).map(([label, count]) => (
                  <div
                    key={label}
                    className="flex justify-between border rounded-lg px-4 py-3"
                  >
                    <span className="font-medium">{label}</span>

                    <span className="font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5">
            <h3 className="font-semibold text-red-700 mb-3">
              Management Insights
            </h3>

            <ul className="space-y-2">
              {data.insights.map((item, index) => (
                <li key={index} className="text-sm text-gray-700">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default LeadAgingReportCard;
